import { useEffect, useMemo, useState } from "react";
import ProfilePhotoField from "@/components/profile/ProfilePhotoField";
import { EXPERT_BACKGROUND_OPTIONS } from "@/lib/expertBackground";

const OTHER_CATEGORY_ID = "__other__";

export const expertFormValues = (user = {}, profile = {}) => ({
  full_name: user.name || profile.name || "",
  photo_url: profile.photo_url || "",
  photo_crop: profile.photo_crop || null,
  category_ids: [
    ...(profile.category_ids?.length ? profile.category_ids : (profile.category_id ? [profile.category_id] : [])),
    ...(profile.custom_category ? [OTHER_CATEGORY_ID] : []),
  ],
  custom_category: profile.custom_category || "",
  background_type: profile.background_type || "",
  headline: profile.headline || "",
  help_summary: profile.help_summary || "",
  bio: profile.bio || "",
  specialties: (profile.specialties || []).join(", "),
  years_experience: profile.years_experience ?? "",
  languages: (profile.languages || []).join(", "),
  availability_note: profile.availability_note || "Efter aftale",
  price_min: profile.price_min ?? "",
});

const listValue = (value) => String(value || "").split(",").map((item) => item.trim()).filter(Boolean);

const FIELD_LABELS = {
  photo_url: "Profilbillede",
  full_name: "Fulde navn",
  category: "Fagområde",
  custom_category: "Dit fagområde",
  background_type: "Faglig baggrund",
  headline: "Professionel overskrift",
  help_summary: "Sådan kan jeg hjælpe dig",
  bio: "Om mig",
  specialties: "Specialer",
  years_experience: "Års erfaring",
  languages: "Sprog",
  availability_note: "Typisk tilgængelighed",
  price_min: "Fra-pris",
};

function validate(form) {
  const errors = {};
  const selected = form.category_ids || [];
  if (!form.photo_url.trim()) errors.photo_url = "Tilføj et tydeligt profilbillede.";
  if (!form.full_name.trim()) errors.full_name = "Skriv dit fulde navn.";
  if (selected.length === 0) errors.category = "Vælg mindst ét fagområde eller vælg Andet.";
  if (selected.length > 5) errors.category = "Du kan højst vælge 5 fagområder.";
  if (selected.includes(OTHER_CATEGORY_ID) && form.custom_category.trim().length < 2) {
    errors.custom_category = "Skriv det fagområde der mangler på listen.";
  }
  if (!form.background_type) errors.background_type = "Vælg den baggrund der bedst beskriver dig.";
  if (form.headline.trim().length < 3) errors.headline = "Skriv en professionel overskrift på mindst 3 tegn.";
  if (form.help_summary.trim().length < 20) errors.help_summary = "Skriv mindst 20 tegn om hvordan du hjælper.";
  if (form.bio.trim().length < 20) errors.bio = "Skriv mindst 20 tegn om dig selv.";
  if (!listValue(form.specialties).length) errors.specialties = "Skriv mindst ét speciale.";
  if (form.years_experience === "" || Number(form.years_experience) < 0) errors.years_experience = "Angiv antal års erfaring.";
  if (!listValue(form.languages).length) errors.languages = "Skriv mindst ét sprog.";
  if (form.availability_note.trim().length < 3) errors.availability_note = "Beskriv kort hvornår du typisk er tilgængelig.";
  if (Number(form.price_min) <= 0) errors.price_min = "Angiv en fra-pris over 0 kr.";
  return errors;
}

export default function ExpertProfileForm({
  initialValues,
  categories,
  categoriesError = "",
  onRetryCategories,
  onSubmit,
  submitLabel,
  submittingLabel = "Gemmer…",
  testId = "expert-profile-form",
}) {
  const [form, setForm] = useState(initialValues);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [requestError, setRequestError] = useState("");

  useEffect(() => setForm(initialValues), [initialValues]);

  const errorEntries = useMemo(
    () => Object.entries(fieldErrors).map(([key, message]) => ({ key, label: FIELD_LABELS[key], message })),
    [fieldErrors],
  );

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[key];
      if (key === "category_ids") delete next.category;
      return next;
    });
  };

  const focusField = (key) => {
    const container = document.getElementById(`expert-field-${key}`);
    container?.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => container?.querySelector("input, textarea, button")?.focus(), 350);
  };

  const submit = async (event) => {
    event.preventDefault();
    setRequestError("");
    const errors = validate(form);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      window.requestAnimationFrame(() => document.getElementById("expert-form-errors")?.scrollIntoView({ behavior: "smooth", block: "center" }));
      return;
    }

    const usesCustomCategory = form.category_ids.includes(OTHER_CATEGORY_ID);
    const payload = {
      full_name: form.full_name.trim(),
      photo_url: form.photo_url.trim(),
      photo_crop: form.photo_crop || null,
      category_ids: form.category_ids.filter((id) => id !== OTHER_CATEGORY_ID),
      custom_category: usesCustomCategory ? form.custom_category.trim() : "",
      background_type: form.background_type,
      headline: form.headline.trim(),
      help_summary: form.help_summary.trim(),
      bio: form.bio.trim(),
      specialties: listValue(form.specialties),
      years_experience: Number(form.years_experience),
      languages: listValue(form.languages),
      availability_note: form.availability_note.trim(),
      price_min: Number(form.price_min),
    };

    setBusy(true);
    try {
      await onSubmit(payload);
    } catch (requestErrorValue) {
      setRequestError(requestErrorValue.response?.data?.detail || "Profilen kunne ikke gemmes.");
    } finally {
      setBusy(false);
    }
  };

  const selectedCategories = form.category_ids || [];
  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid={testId}>
      {errorEntries.length > 0 && (
        <div id="expert-form-errors" className="rounded-xl border border-[#E8B4AE] bg-[#FFF1EE] p-4 text-sm text-[#7A271A]" role="alert" tabIndex="-1">
          <div className="font-semibold">Der mangler {errorEntries.length} {errorEntries.length === 1 ? "oplysning" : "oplysninger"}</div>
          <p className="mt-1">Tryk på et felt for at gå direkte til det:</p>
          <ul className="mt-2 space-y-1">
            {errorEntries.map(({ key, label, message }) => (
              <li key={key}>
                <button type="button" onClick={() => focusField(key)} className="text-left underline decoration-[#B42318]/40 underline-offset-2 hover:decoration-[#B42318]">
                  <span className="font-medium">{label}:</span> {message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div id="expert-field-photo_url">
        <ProfilePhotoField
          value={form.photo_url}
          name={form.full_name}
          onChange={(value, crop) => {
            setForm((current) => ({ ...current, photo_url: value, photo_crop: crop }));
            setFieldErrors((current) => { const next = { ...current }; delete next.photo_url; return next; });
          }}
          expert
        />
        {fieldErrors.photo_url && <FieldError>{fieldErrors.photo_url}</FieldError>}
      </div>

      <section className="card-outline p-5 sm:p-6">
        <h2 className="font-display text-xl font-semibold text-brand-ink">Din profil</h2>
        <p className="mt-1 text-sm text-brand-muted">Skriv konkret så kunden hurtigt kan vurdere om du kan løse problemet.</p>
        <div className="mt-5">
          <Field id="expert-field-full_name" label="Fulde navn" value={form.full_name} onChange={(value) => update("full_name", value)} error={fieldErrors.full_name}/>
        </div>
        <div id="expert-field-category" className="mt-4">
          <div className="text-sm font-medium text-brand-ink">Vælg de områder du kan rådgive professionelt indenfor</div>
          <p className="mt-0.5 text-xs text-brand-muted">Du kan vælge op til 5 fagområder. Mangler dit område så vælg Andet.</p>
          {categoriesError && <div className="mt-2 rounded-xl border border-[#B7791F] bg-[#FFF7E8] px-3 py-2 text-sm text-[#7A4B10]" role="alert">Fagområderne kunne ikke indlæses. {onRetryCategories && <button type="button" onClick={onRetryCategories} className="ml-1 font-semibold underline">Prøv igen</button>}</div>}
          <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Ekspertkategorier" aria-invalid={Boolean(fieldErrors.category)}>
            {[...categories, { id: OTHER_CATEGORY_ID, name: "Andet" }].map((category) => {
              const selected = selectedCategories.includes(category.id);
              const disabled = !selected && selectedCategories.length >= 5;
              return <button key={category.id} type="button" disabled={disabled} aria-pressed={selected} onClick={() => update("category_ids", selected ? selectedCategories.filter((id) => id !== category.id) : [...selectedCategories, category.id])} className={`rounded-full border px-3 py-2 text-sm transition-colors ${selected ? "border-brand-green bg-brand-soft text-brand-green" : fieldErrors.category ? "border-[#B42318] bg-white text-brand-body" : "border-brand-line bg-white text-brand-body"} disabled:cursor-not-allowed disabled:opacity-40`} data-testid={`expert-category-${category.id}`}>{category.name}</button>;
            })}
          </div>
          {fieldErrors.category && <FieldError>{fieldErrors.category}</FieldError>}
          <div className="mt-2 text-xs text-brand-muted">{selectedCategories.length} af 5 valgt</div>
        </div>
        {selectedCategories.includes(OTHER_CATEGORY_ID) && (
          <div className="mt-4">
            <Field id="expert-field-custom_category" label="Hvilket fagområde mangler?" value={form.custom_category} onChange={(value) => update("custom_category", value)} placeholder="Fx Hundetræning eller lydteknik" error={fieldErrors.custom_category}/>
            <p className="mt-1 text-xs text-brand-muted">Vi bruger svaret til at vurdere om området skal oprettes som en fast kategori.</p>
          </div>
        )}
        <fieldset id="expert-field-background_type" className="mt-5">
          <legend className="text-sm font-medium text-brand-ink">Hvordan vil du beskrive din faglige baggrund?</legend>
          <p className="mt-0.5 text-xs text-brand-muted">Vælg det der passer bedst. Oplysningen er selvangivet og er ikke det samme som en verificeret kvalifikation.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {EXPERT_BACKGROUND_OPTIONS.map((option) => {
              const selected = form.background_type === option.value;
              return (
                <label key={option.value} className={`cursor-pointer rounded-xl border p-3 transition-colors ${selected ? "border-brand-green bg-brand-soft" : fieldErrors.background_type ? "border-[#B42318] bg-white" : "border-brand-line bg-white"}`}>
                  <input type="radio" name="background_type" value={option.value} checked={selected} onChange={() => update("background_type", option.value)} className="sr-only"/>
                  <span className="block text-sm font-semibold text-brand-ink">{option.label}</span>
                  <span className="mt-1 block text-xs leading-5 text-brand-muted">{option.description}</span>
                </label>
              );
            })}
          </div>
          {fieldErrors.background_type && <FieldError>{fieldErrors.background_type}</FieldError>}
        </fieldset>
        <div className="mt-4">
          <Field id="expert-field-headline" label="Professionel overskrift" value={form.headline} onChange={(value) => update("headline", value)} placeholder="Fx Automekaniker med speciale i Mercedes" error={fieldErrors.headline}/>
        </div>
        <TextArea id="expert-field-help_summary" label="Sådan kan jeg hjælpe dig" value={form.help_summary} onChange={(value) => update("help_summary", value)} maxLength={700} rows={4} hint="Beskriv hvilke problemer du typisk løser og hvordan kunden får hjælp." placeholder="Jeg hjælper dig med at finde fejlen trin for trin via billeder, chat og video…" error={fieldErrors.help_summary}/>
        <TextArea id="expert-field-bio" label="Om mig" value={form.bio} onChange={(value) => update("bio", value)} maxLength={3000} rows={6} hint="Fortæl om din baggrund, erfaring og arbejdsform." placeholder="Jeg har arbejdet med…" error={fieldErrors.bio}/>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field id="expert-field-specialties" label="Specialer" value={form.specialties} onChange={(value) => update("specialties", value)} placeholder="Bremser, fejlfinding, Mercedes" hint="Adskil med komma" error={fieldErrors.specialties}/>
          <Field id="expert-field-years_experience" label="Års erfaring" type="number" min="0" value={form.years_experience} onChange={(value) => update("years_experience", value)} error={fieldErrors.years_experience}/>
          <Field id="expert-field-languages" label="Sprog" value={form.languages} onChange={(value) => update("languages", value)} placeholder="Dansk, engelsk" hint="Adskil med komma" error={fieldErrors.languages}/>
          <Field id="expert-field-availability_note" label="Typisk tilgængelighed" value={form.availability_note} onChange={(value) => update("availability_note", value)} placeholder="Fx Hverdage efter kl. 16 og weekender" error={fieldErrors.availability_note}/>
        </div>
      </section>

      <section className="card-outline p-5 sm:p-6">
        <h2 className="font-display text-xl font-semibold text-brand-ink">Pris</h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-brand-muted">Kunden ser kun din fra-pris. Når du svarer på en forespørgsel sender du et konkret tilbud med forventet tid og samlet pris.</p>
        <div className="mt-5 max-w-sm">
          <Field id="expert-field-price_min" label="Fra-pris (kr.)" type="number" min="1" value={form.price_min} onChange={(value) => update("price_min", value)} hint="Den laveste samlede pris du normalt vil tilbyde en opgave til." error={fieldErrors.price_min}/>
        </div>
        <div className="mt-4 rounded-xl bg-brand-soft p-4 text-sm leading-6 text-brand-body">
          Eksempel: Din profil viser “Fra 200 kr.”. Til en konkret opgave kan du sende en fast pris eller beregne en samlet pris ud fra forventet tid og den timepris du vælger i tilbuddet.
        </div>
      </section>

      {requestError && <div className="rounded-xl bg-[#FFF1EE] px-4 py-3 text-sm text-[#991B1B]" role="alert">{requestError}</div>}
      <button type="submit" disabled={busy} className="btn-primary w-full sm:w-auto disabled:opacity-50">{busy ? submittingLabel : submitLabel}</button>
    </form>
  );
}

function FieldError({ children }) {
  return <span className="mt-1 block text-xs font-medium text-[#B42318]">{children}</span>;
}

function TextArea({ id, label, value, onChange, placeholder, hint, rows, maxLength, error }) {
  return <label id={id} className="mt-4 block">
    <span className="text-sm font-medium text-brand-ink">{label}</span>
    {hint && <span className="mt-0.5 block text-xs text-brand-muted">{hint}</span>}
    <textarea aria-invalid={Boolean(error)} maxLength={maxLength} rows={rows} value={value} onChange={(event) => onChange(event.target.value)} className={`mt-2 w-full resize-y rounded-xl border bg-white px-3 py-2.5 outline-none focus:ring-4 ${error ? "border-[#B42318] focus:border-[#B42318] focus:ring-[#B42318]/10" : "border-brand-line focus:border-brand-fresh focus:ring-brand-fresh/10"}`} placeholder={placeholder}/>
    {error && <FieldError>{error}</FieldError>}
    <span className="mt-1 block text-right text-[11px] text-brand-muted">{String(value || "").length}/{maxLength}</span>
  </label>;
}

function Field({ id, label, value, onChange, placeholder, hint, type = "text", min, error }) {
  return <label id={id} className="block">
    <span className="text-sm font-medium text-brand-ink">{label}</span>
    <input aria-invalid={Boolean(error)} type={type} min={min} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className={`mt-1 min-h-11 w-full rounded-xl border bg-white px-3 py-2.5 outline-none focus:ring-4 ${error ? "border-[#B42318] focus:border-[#B42318] focus:ring-[#B42318]/10" : "border-brand-line focus:border-brand-fresh focus:ring-brand-fresh/10"}`}/>
    {error && <FieldError>{error}</FieldError>}
    {hint && <span className="mt-1 block text-xs text-brand-muted">{hint}</span>}
  </label>;
}

